import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2, Plus, X } from "lucide-react";

type Plan = {
  id: string;
  name: string;
  price: number;
  description: string;
  is_popular: boolean;
};

type Feature = {
  id: string;
  feature_name: string;
  feature_order: number;
};

type EditPricingPlanDialogProps = {
  plan: Plan | null;
  features: Feature[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function EditPricingPlanDialog({
  plan,
  features: initialFeatures,
  open,
  onOpenChange,
  onSuccess,
}: EditPricingPlanDialogProps) {
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [isPopular, setIsPopular] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    if (plan) {
      setPrice(plan.price.toString());
      setDescription(plan.description);
      setIsPopular(plan.is_popular || false);
      setFeatures(initialFeatures.map((f) => f.feature_name));
    }
  }, [plan, initialFeatures]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;

    setLoading(true);
    try {
      // If marking this plan as popular, unmark all others first
      if (isPopular) {
        await supabase
          .from("pricing_plans")
          .update({ is_popular: false })
          .neq("id", plan.id);
      }

      // Update plan
      const { error: planError } = await supabase
        .from("pricing_plans")
        .update({
          price: parseFloat(price),
          description,
          is_popular: isPopular,
        })
        .eq("id", plan.id);

      if (planError) throw planError;

      // Delete existing features
      const { error: deleteError } = await supabase
        .from("plan_features")
        .delete()
        .eq("plan_id", plan.id);

      if (deleteError) throw deleteError;

      // Insert new features
      const featuresToInsert = features.map((feature, index) => ({
        plan_id: plan.id,
        feature_name: feature,
        feature_order: index,
      }));

      const { error: insertError } = await supabase
        .from("plan_features")
        .insert(featuresToInsert);

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "Pricing plan updated successfully",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature("");
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {plan?.name} Plan</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="price">Price (£/year)</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="is-popular"
              checked={isPopular}
              onCheckedChange={(checked) => setIsPopular(checked as boolean)}
            />
            <Label htmlFor="is-popular" className="text-sm cursor-pointer">
              Mark as "Most Popular" plan
            </Label>
          </div>

          <div className="space-y-2">
            <Label>Features</Label>
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input value={feature} readOnly className="flex-1" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeFeature(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add new feature"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addFeature();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addFeature}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
